import { cn } from "@newxsystems/shared";
import { TextEffect } from "../../../../text-effects/TextEffect.client";
import { DaylightAction } from "../DaylightAction/DaylightAction";
import { DaylightRichText } from "../DaylightRichText/DaylightRichText";
import type { IDaylightCommonContentProps } from "./DaylightCommonContent.props";

export const DaylightCommonContent = ({
  alignment,
  buttons,
  className,
  description,
  eyebrow,
  heading,
  headingEffect,
  headingAs = "h2",
  headingClassName,
  layout = "split",
  reveal = false,
  revealHeading,
  tone = "onDark",
}: IDaylightCommonContentProps) => {
  if (!eyebrow && !heading && !description && !buttons?.length) return null;

  const Heading = headingAs;
  const centered = alignment === "center";
  const onAccent = tone === "onAccent";
  const copy = (
    <>
      {eyebrow ? (
        <p className={cn("signal-label", onAccent ? "text-primary-foreground" : "text-signal")}>
          {eyebrow}
        </p>
      ) : null}
      {heading ? (
        <Heading
          className={cn(
            "max-w-[20ch] break-words font-daylight-display text-[clamp(2rem,4.2vw,3.125rem)] font-semibold leading-[1.04] tracking-[-0.03em] text-balance",
            onAccent ? "text-primary-foreground" : "text-foreground",
            centered && "mx-auto",
            headingClassName,
          )}
          data-reveal-enter={revealHeading}
        >
          <TextEffect effect={headingEffect} text={heading} />
        </Heading>
      ) : null}
      {description ? (
        <DaylightRichText
          className={cn(
            "max-w-[58ch]",
            onAccent
              ? "text-primary-foreground/75 [&_a]:text-primary-foreground [&_strong]:text-primary-foreground"
              : "text-ash",
          )}
          content={description}
        />
      ) : null}
    </>
  );

  const actions = buttons?.length ? (
    <div className={cn("flex flex-wrap gap-3", centered && "justify-center")}>
      {buttons.map((button, index) => (
        <DaylightAction action={button} key={`${button.href}-${index}`} tone={tone} />
      ))}
    </div>
  ) : null;

  // `split` means copy on the left and actions on the right. Without actions the
  // second track is empty, so the copy would be squeezed into half the row — and
  // inside a narrow column the two tracks leave the heading about one word wide.
  // Both cases want the stacked layout, so the split only survives when it has
  // something to put opposite the copy.
  const split = !centered && layout === "split" && Boolean(actions);

  if (!split) {
    return (
      <div
        className={cn(
          "flex flex-col gap-5",
          centered && "mx-auto max-w-[52rem] items-center text-center",
          className,
        )}
        data-reveal={reveal ? "stack" : undefined}
      >
        {copy}
        {actions}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "grid gap-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end lg:gap-20",
        className,
      )}
    >
      <div className="flex flex-col gap-4" data-reveal={reveal ? "stack" : undefined}>
        {copy}
      </div>
      {/* Actions hold the fourth slot of the sequence, so they land 300ms after
          the copy starts whether the copy runs to two lines or three. */}
      <div
        className="flex flex-wrap gap-3 lg:justify-end lg:pb-1.5"
        data-reveal={reveal ? "stack" : undefined}
        data-reveal-delay={reveal ? 300 : undefined}
      >
        {actions}
      </div>
    </div>
  );
};

DaylightCommonContent.displayName = "Content::DaylightCommonContent";
